import React, { useState, useRef } from "react";
import './ModelUpload.css';
import {
    Box, Paper, Typography, Button, IconButton, List, ListItem, Autocomplete,
    TextField, FormGroup, FormControlLabel, Checkbox, Modal, Divider,
    Tooltip, Alert, Snackbar
} from "@mui/material";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import Datagrid from "../Datagrid/Datagrid";
import CloseIcon from '@mui/icons-material/Close';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import Swal from "sweetalert2";
import LinearProgress from '@mui/material/LinearProgress';
import customHttp from "../../services/customHTTP";
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';

function LinearProgressWithLabel(props) {
    return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ width: "100%", mr: 1 }}>
                <LinearProgress variant="determinate" {...props} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {`${Math.round(props.value)}%`}
                </Typography>
            </Box>
        </Box>
    );
}

const ModelUpload = ({
    open,
    onClose,
    allMachines,
    uploadModelColumns,
    uploadModelButtons,
    fetchMachines,
    newModel,
    setNewModel
}) => {
    const [files, setFiles] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [fileUploadProgress, setFileUploadProgress] = useState(0);
    const [showFileUploadProgress, setShowFileUploadProgress] = useState(false);
    const [isChangePart, setIsChangePart] = useState(false);
    const [machineSelected, setMachineSelected] = useState(null);

    const [showFilesPopup, setShowFilesPopup] = useState(false);
    const [selectedMachineForFiles, setSelectedMachineForFiles] = useState(null);
    const [modelFiles, setModelFiles] = useState([]);
    const [replaceFileIndex, setReplaceFileIndex] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fileInputRef = useRef(null);

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => setIsDragging(false);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files.length > 0) {
            handleFiles(e.dataTransfer.files);
        }
    };

    const handleModelFileChange = (e) => {
        if (e.target.files.length > 0) {
            handleFiles(e.target.files);
        }
    };

    const handleFiles = (fileList) => {
        const newFiles = Array.from(fileList);
        if (isChangePart) {
            // Only one file allowed, replace
            setFiles(newFiles.slice(0, 1));
        } else {
            // Append, avoid duplicates by name
            setFiles((prevFiles) => {
                const existingNames = new Set(prevFiles.map(f => f.name));
                const filteredNewFiles = newFiles.filter(f => !existingNames.has(f.name));
                return [...prevFiles, ...filteredNewFiles];
            });
        }
    };

    const handleRemoveFile = (index) => {
        const newFiles = [...files];
        newFiles.splice(index, 1);
        setFiles(newFiles);
    };

    const handleRowClick = async (machine) => {
        console.log('Row Clciked', machine)
        setSelectedMachineForFiles(machine);
        setShowFilesPopup(true);
        // Fetch files for this machine
        const res = await customHttp.get(`/machines/models/${machine.row.machine_number}/files`);
        setModelFiles(res.data.files || []);
    };

    const handleCheckboxChange = () => setIsChangePart(!isChangePart);

    const handleUpload = async () => {
		if (files.length === 0) {
			Swal.fire({
				icon: "error",
				title: "Error",
				text: "Please select a file and try again.",
			});
			return;
		}

		if (isChangePart && files.length > 1) {
			Swal.fire({
				icon: "error",
				title: "Error",
				text: "Only one file can be uploaded for a Change Part.",
			});
			return;
		}

		setUploading(true);
		setShowFileUploadProgress(true);
		const formData = new FormData();

		if (isChangePart) {
			// Single file upload with `_CP` suffix
			const file = files[0];
			const ext = file.name.substring(file.name.lastIndexOf("."));
			const fileName = `${machineSelected.machine_number}_CP${ext}`;
			const renamedFile = new File([file], fileName, { type: file.type });
			formData.append("files", renamedFile);
		} else {
			// Multiple files upload with numbered suffixes
			files.forEach((file, index) => {
				const ext = file.name.substring(file.name.lastIndexOf("."));
				const paddedIndex = String(index + 1).padStart(2, "0");
				const fileName = `${machineSelected.machine_number}_${paddedIndex}${ext}`;
				const renamedFile = new File([file], fileName, { type: file.type });
				formData.append("files", renamedFile);
			});
		}

		formData.append("machineNumber", machineSelected.machine_number);
		formData.append("isChangePart", isChangePart.toString());

		try {
			const response = await customHttp.post("/machines/upload", formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
				onUploadProgress: (progressEvent) => {
					const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
					console.log(`Upload progress: ${percentCompleted}%`);
					setFileUploadProgress(percentCompleted);
				},
			});

			if (response.status === 200) {
				Swal.fire({
					icon: "success",
					title: "Success",
					text: "Model file(s) uploaded successfully.",
				});
				fetchMachines();
			} else {
				throw new Error("Upload failed with non-200 status.");
			}
		} catch (error) {
			Swal.fire({
				icon: "error",
				title: "Error",
				text: "Failed to upload model file(s). Please try again.",
			});
			console.error("Error during upload:", error);
		} finally {
			setUploading(false);
			setShowFileUploadProgress(false);
			setNewModel(false);
		}
	};

    const handleDragStart = (e, index) => {
        e.dataTransfer.setData("text/plain", index);
        setIsDragging(true);
    };

    const handleDropOrder = (e, dropIndex) => {
        e.preventDefault();
        const dragIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);
        setIsDragging(false);

        if (dragIndex !== dropIndex) {
            const updatedFiles = [...files];
            const [draggedFile] = updatedFiles.splice(dragIndex, 1);
            updatedFiles.splice(dropIndex, 0, draggedFile);
            setFiles(updatedFiles);
        }
    };

    const sortedFiles = [
        ...modelFiles.filter(f => !/_CP\.[^/.]+$/.test(f.name)),
        ...modelFiles.filter(f => /_CP\.[^/.]+$/.test(f.name))
    ];
    let dividerInserted = false;

    return (
        <>
            <Box display="flex" flexDirection="row" width="100%" justifyContent="space-between" mb={2}>
                    <Box display="flex" flexDirection="column" alignItems="flex-start">
                        <Typography variant="h6">Manage Machine Models</Typography>
                        <Typography variant="body1">
                            Admin can upload machine models or change parts for a machine.
                        </Typography>
                    </Box>
                </Box>

                <Datagrid
                    data={allMachines}
                    columns={uploadModelColumns}
                    key="machine_id"
                    rowIdField="machine_id"
                    customButtons={uploadModelButtons}
                    pageSize={5}
                    sortBy={[{ field: "machine_name", sort: "asc" }]}
                    rowClick={true}
                    onRowClick={handleRowClick}
                />
            <Modal
                open={newModel}
                onClose={onClose}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}>
                <Box
                    className="csv-upload-container"
                    sx={{
                        width: "100%",
                        maxWidth: 450,
                        m: 2,
                        position: "relative",
                        outline: "none",
                    }}>
                    <Paper
                        elevation={3}
                        className={`upload-card ${isDragging ? "dragging" : ""}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        sx={{
                            width: "100%",
                            padding: 3,
                            textAlign: "center",
                            border: "2px dashed #cccccc",
                            borderRadius: 4,
                            backgroundColor: isDragging ? "#f0f7ff" : "#f2f2f2",
                            pointerEvents: "auto",
                            transition: "all 0.2s ease",
                        }}>
                        <FileUploadIcon sx={{ fontSize: 40, color: "#888" }} />
                        <Typography variant="h6" gutterBottom>
                            {isChangePart ? "Upload Change Part Models" : "Upload Models"}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Drag and drop files here (only .asm or .stp)
                        </Typography>

                        {files.length > 0 && (
                            <Box
                                sx={{
                                    mt: 2,
                                    mb: 2,
                                    width: "100%",
                                    backgroundColor: "#f2f2f2",
                                }}>
                                <List
                                    sx={{
                                        width: "100%",
                                        bgcolor: isDragging ? "#f0f7ff" : "#f2f2f2",
                                        p: 1,
                                        borderRadius: 2,
                                    }}>
                                    {files.map((file, index) => (
                                        <ListItem
                                            key={index}
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                backgroundColor: "#ffffff",
                                                mb: 1,
                                                p: 1,
                                                px: 2,
                                                borderRadius: 2,
                                                boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                                                transition: "background-color 0.3s ease",
                                            }}>
                                            <Box
                                                component="span"
                                                onMouseDown={(e) => e.stopPropagation()}
                                                draggable
                                                onDragOver={(e) => e.preventDefault()}
                                                onDragStart={(e) => handleDragStart(e, index)}
                                                onDrop={(e) => handleDropOrder(e, index)}
                                                sx={{
                                                    cursor: "grab",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    pr: 1,
                                                    color: "#888",
                                                }}>
                                                <DragIndicatorIcon />
                                            </Box>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    flexGrow: 1,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}>
                                                {file.name}
                                            </Typography>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRemoveFile(index)}
                                                sx={{ color: "#f44336", ml: 1 }}>
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}

                        <input
                            accept=".asm,.stp,.step"
                            style={{ display: "none" }}
                            id="raised-button-file"
                            disabled={machineSelected === null}
                            type="file"
                            multiple
                            ref={fileInputRef}
                            onChange={handleModelFileChange}
                        />
                        <label htmlFor="raised-button-file">
                            <Button
                                variant="contained"
                                component="span"
                                startIcon={<AttachFileIcon />}
                                sx={{ marginTop: 1, marginBottom: 2 }}
                                disabled={machineSelected === null}>
                                Select Files
                            </Button>
                        </label>

                        <Autocomplete
                            sx={{ width: "100%" }}
                            options={allMachines}
                            getOptionLabel={(option) =>
                                `${option.machine_number} - ${option.machine_name}`
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Machine"
                                    variant="outlined"
                                    size="small"
                                />
                            )}
                            onChange={(event, value) => setMachineSelected(value)}
                            isOptionEqualToValue={(option, value) =>
                                option.machine_number === value?.machine_number
                            }
                        />

                        <FormGroup row sx={{ justifyContent: "center", mt: 1 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isChangePart}
                                        onChange={handleCheckboxChange}
                                        color="primary"
                                    />
                                }
                                label="Is Change Part"
                                labelPlacement="end"
                            />
                        </FormGroup>

                        {showFileUploadProgress && (
                            <Box sx={{ width: "100%", mt: 2 }}>
                                <LinearProgressWithLabel value={fileUploadProgress} />
                            </Box>
                        )}

                        {files.length > 0 && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    mt: 2,
                                    width: "100%",
                                }}>
                                <Button
                                    variant="outlined"
                                    color="inherit"
                                    onClick={onClose}
                                    sx={{ mr: 1 }}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleUpload}
                                    disabled={uploading}>
                                    {uploading ? "Uploading..." : "Upload"}
                                </Button>
                            </Box>
                        )}
                    </Paper>
                </Box>
            </Modal>

            <Modal 
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1200, // Lower than SweetAlert2
                }} 
                open={showFilesPopup}
                onClose={() => {
                    setShowFilesPopup(false);
                    fetchMachines();
                }}
            >
                <Box sx={{
                    width: 400, bgcolor: "#fff", p: 3, borderRadius: 2,
                    boxShadow: 24, outline: "none"
                }}>
                    <Box sx={{ textAlign: "center", mb: 2 }}>
                        <Typography variant="h5" fontWeight="bold">
                            Edit Model Files
                        </Typography>
                        <Typography variant="subtitle1">
                            {selectedMachineForFiles?.row.machine_name}
                        </Typography>
                    </Box>
                        <List>
                            {sortedFiles.map((file) => {
                                const isCP = /_CP\.[^/.]+$/.test(file.name);
                                let divider = null;

                                // Insert divider before the first _CP file
                                if (isCP && !dividerInserted) {
                                    dividerInserted = true;
                                    divider = (
                                        <React.Fragment key="cp-divider">
                                            <Divider sx={{ my: 2 }} />
                                            <Typography
                                                variant="subtitle2"
                                                align="center"
                                                sx={{ color: 'text.secondary', mb: 1 }}
                                            >
                                                Change Part Files
                                            </Typography>
                                        </React.Fragment>
                                    );
                                }

                                return (
                                    <React.Fragment key={file.name}>
                                        {divider}
                                        <ListItem sx={{ display: "flex", alignItems: "center" }}>
                                            <Typography sx={{ flex: 1 }}>{file.name}</Typography>
                                            <Tooltip title={'Delete File'}>
                                                <IconButton
                                                    color="error"
                                                    size="small"
                                                    onClick={async () => {
                                                        const result = await Swal.fire({
                                                            title: 'Are you sure?',
                                                            text: `Delete file "${file.name}"?`,
                                                            icon: 'warning',
                                                            showCancelButton: true,
                                                            confirmButtonColor: '#d33',
                                                            cancelButtonColor: '#3085d6',
                                                            confirmButtonText: 'Yes, delete it!'
                                                        });
                                                        if (result.isConfirmed) {
                                                            try {
                                                                const res = await customHttp.delete(`/machines/models/${selectedMachineForFiles.row.machine_number}/files/${file.name}`);
                                                                if (res.status === 200) {
                                                                    setSnackbar({ open: true, message: 'File deleted successfully.', severity: 'success' });
                                                                } else {
                                                                    setSnackbar({ open: true, message: 'Failed to delete file.', severity: 'error' });
                                                                }
                                                                const filesRes = await customHttp.get(`/machines/models/${selectedMachineForFiles.row.machine_number}/files`);
                                                                setModelFiles(filesRes.data.files || []);
                                                            } catch (err) {
                                                                setSnackbar({ open: true, message: 'Error deleting file.', severity: 'error' });
                                                            }
                                                        }
                                                    }}
                                                    sx={{ mr: 1 }}
                                                    aria-label="delete"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={'Replace File'}>
                                                <IconButton
                                                    size="small"
                                                    component="label"
                                                    aria-label="replace"
                                                    sx={{ color: "#1976d2" }}
                                                >
                                                    <DriveFileRenameOutlineIcon />
                                                    <input
                                                        type="file"
                                                        accept=".asm,.stp,.step"
                                                        hidden
                                                        onChange={async (e) => {
                                                            try {
                                                                const fileObj = e.target.files[0];
                                                                if (!fileObj) return;
                                                                const formData = new FormData();
                                                                formData.append("file", fileObj);
                                                                const res = await customHttp.post(
                                                                    `/machines/models/${selectedMachineForFiles.row.machine_number}/files/${file.name}/replace`,
                                                                    formData,
                                                                    { headers: { "Content-Type": "multipart/form-data" } }
                                                                );
                                                                if (res.status === 200) {
                                                                    setSnackbar({ open: true, message: 'File replaced successfully.', severity: 'success' });
                                                                } else {
                                                                    setSnackbar({ open: true, message: 'Failed to replace file.', severity: 'error' });
                                                                }
                                                                const filesRes = await customHttp.get(`/machines/models/${selectedMachineForFiles.row.machine_number}/files`);
                                                                setModelFiles(filesRes.data.files || []);
                                                                setReplaceFileIndex(null);
                                                            } catch (err) {
                                                                setSnackbar({ open: true, message: 'Error replacing file.', severity: 'error' });
                                                            }
                                                        }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        </ListItem>
                                    </React.Fragment>
                                );
                            })}
                        </List>
                    <Box display="flex" justifyContent="flex-end" mt={2}>
                        <Button variant="contained" onClick={() => {fetchMachines(); setShowFilesPopup(false)}}>Close</Button>
                    </Box>
                </Box>
            </Modal>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default ModelUpload;